import type { ReactNode } from "react";

import type { TeamPreferences } from "../../api/types";
import { Select } from "../../ui/Select";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { Toggle } from "../../ui/Toggle";
import { audienceOptions, tocPositionOptions, useWorkspaceSettings } from "./workspace";

/**
 * Workspace-wide switches. Each one writes a single field or a single preference
 * key — `teams.update` merges the preference bag key by key — and the write is
 * optimistic, so the switch moves at once and snaps back with an explanation if
 * the server refuses.
 */
export function SecuritySettingsScreen(): ReactNode {
  const { team, canUpdate, save } = useWorkspaceSettings();
  const preferences: TeamPreferences = team?.preferences ?? {};

  const setPreference = (patch: TeamPreferences): void => {
    save({ preferences: patch });
  };

  return (
    <SettingsPage
      title="Security"
      description="What people in this workspace may do, and what may leave it."
    >
      <SettingsSection title="Sharing outside the workspace">
        <Toggle
          label="Public link sharing"
          description="The master switch. With it off, no document can be shared publicly and existing links stop serving."
          checked={team?.sharing ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            save({ sharing: checked });
          }}
        />
        <Toggle
          label="Rich link previews"
          description="Render embeds from third-party services inside documents."
          checked={team?.documentEmbeds ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            save({ documentEmbeds: checked });
          }}
        />
        <Toggle
          label="Viewers can export"
          description="Give viewers and guests the ability to download and export."
          checked={preferences.viewersCanExport ?? true}
          disabled={!canUpdate}
          onChange={(checked) => {
            setPreference({ viewersCanExport: checked });
          }}
        />
      </SettingsSection>

      <SettingsSection title="Sign-in">
        <Toggle
          label="Email sign-in"
          description="Allow signing in with an emailed link rather than an identity provider."
          checked={team?.guestSignin ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            save({ guestSignin: checked });
          }}
        />
      </SettingsSection>

      <SettingsSection
        title="What members may do"
        description="Administrators may always do these."
      >
        <Toggle
          label="Members can create collections"
          checked={team?.memberCollectionCreate ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            save({ memberCollectionCreate: checked });
          }}
        />
        <Toggle
          label="Members can create workspaces"
          description="Has no effect on a self-hosted installation."
          checked={team?.memberTeamCreate ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            save({ memberTeamCreate: checked });
          }}
        />
        <Toggle
          label="Members can invite"
          description="Without this, only administrators may invite people."
          checked={preferences.membersCanInvite ?? false}
          disabled={!canUpdate}
          onChange={(checked) => {
            setPreference({ membersCanInvite: checked });
          }}
        />
        <Toggle
          label="Members can create API keys"
          description="Turning this off leaves existing member keys working, but under administrator control."
          checked={preferences.membersCanCreateApiKey ?? true}
          disabled={!canUpdate}
          onChange={(checked) => {
            setPreference({ membersCanCreateApiKey: checked });
          }}
        />
        <Toggle
          label="Members can delete their account"
          checked={preferences.membersCanDeleteAccount ?? true}
          disabled={!canUpdate}
          onChange={(checked) => {
            setPreference({ membersCanDeleteAccount: checked });
          }}
        />
      </SettingsSection>

      <SettingsSection title="Visibility and comments">
        <div className="flex flex-col gap-4">
          <Select
            label="Who can see email addresses"
            description="Administrators and the person themselves always can."
            value={preferences.emailDisplay ?? "members"}
            options={audienceOptions}
            disabled={!canUpdate}
            onChange={(value) => {
              setPreference({ emailDisplay: value });
            }}
          />
          <Select
            label="Who can comment"
            value={preferences.commenting ?? "members"}
            options={audienceOptions}
            disabled={!canUpdate}
            onChange={(value) => {
              setPreference({ commenting: value });
            }}
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Reading and email">
        <Toggle
          label="Content previews in emails"
          description="Notification emails quote the document or comment rather than only linking to it."
          checked={preferences.previewsInEmails ?? true}
          disabled={!canUpdate}
          onChange={(checked) => {
            setPreference({ previewsInEmails: checked });
          }}
        />
        <div className="mt-4">
          <Select
            label="Table of contents position"
            value={preferences.tocPosition ?? "left"}
            options={tocPositionOptions}
            disabled={!canUpdate}
            onChange={(value) => {
              setPreference({ tocPosition: value });
            }}
          />
        </div>
      </SettingsSection>
    </SettingsPage>
  );
}
